import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { User, Mail, Phone, Shield, Save, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cognitoAuthService } from '../services/cognitoAuthService';
import { useNavigate } from 'react-router-dom';
import { auditProfileChange } from '../lib/auditClient';
import { parseJWTToken } from '../utils/tokenUtils';

interface UserProfile {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailVerified: boolean;
}

// Add this interface for better typing
interface CognitoUserAttributes {
  email?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  email_verified?: string;
  [key: string]: string | undefined;
}

type ChangeMap = Record<string, { old: any; new: any }>;

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser, refreshUserProfile } = useAuth();
  const [profile, setProfile] = useState<UserProfile>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailVerified: false
  });
  const [originalProfile, setOriginalProfile] = useState<UserProfile>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailVerified: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [emailChanged, setEmailChanged] = useState(false);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingVerification, setSendingVerification] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  useEffect(() => {
    loadUserProfile();
  }, []);

  // Handle navigation back from external sites (like Stripe)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('🔄 Profile page became visible - user may have navigated back from external site');
        // Small delay to ensure React has time to re-render
        setTimeout(() => {
          loadUserProfile();
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);



  const loadUserProfile = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');

    if (!accessToken || !user) {
      setError('No access token available');
      setLoading(false);
      return;
    }

    // Safety timeout - much longer than before
    let timeoutId: NodeJS.Timeout;

    try {
      setLoading(true);
      setError(null);
      // Safety timeout - much longer than before
      timeoutId = setTimeout(() => {
        setLoading(false);
        setError('Profile loading is taking longer than expected. Please try refreshing the page.');
      }, 30000); // 30 second timeout

      // Check if user is OAuth user (has identities in token)
      const isOAuthUser = idToken && parseJWTToken(idToken)?.identities;

      if (isOAuthUser && idToken) {
        // For OAuth users, use data from ID token instead of API call
        const tokenData = parseJWTToken(idToken);

        const userProfile: UserProfile = {
          email: tokenData?.email || user.email,
          firstName: tokenData?.given_name || '',
          lastName: tokenData?.family_name || '',
          phoneNumber: '', // OAuth users don't have phone numbers initially
          emailVerified: true // OAuth users are verified by the provider
        };

        setProfile(userProfile);
        setOriginalProfile(userProfile);
      } else {
        // For regular users, use the API call
        const userData = await cognitoAuthService.getCurrentUser(accessToken);

        const attributes = userData.attributes as CognitoUserAttributes;

        const userProfile: UserProfile = {
          email: attributes?.email || user.email,
          firstName: attributes?.given_name || '',
          lastName: attributes?.family_name || '',
          phoneNumber: attributes?.phone_number || '',
          emailVerified: attributes?.email_verified === 'true'
        };

        setProfile(userProfile);
        setOriginalProfile(userProfile);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load profile';
      setError(errorMessage);

      // If it's an authentication error, suggest signing in again
      if (errorMessage.includes('token') || errorMessage.includes('auth') || errorMessage.includes('expired')) {
        setError('Your session may have expired. Please try signing out and signing in again.');
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setLoading(false);
    }
  };



  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    
    // Check if email changed
    if (field === 'email' && value !== originalProfile.email) {
      setEmailChanged(true);
    } else if (field === 'email' && value === originalProfile.email) {
      setEmailChanged(false);
    }
    
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const validateForm = (): boolean => {
    if (!profile.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!profile.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!profile.email.trim()) {
      setError('Email is required');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profile.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Phone number validation (if provided)
    if (profile.phoneNumber && !profile.phoneNumber.match(/^\+?[\d\s\-()]+$/)) {
      setError('Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const computeChanges = (oldP: UserProfile, newP: UserProfile): ChangeMap => {
    const changes: ChangeMap = {};
    (['firstName','lastName','email','phoneNumber'] as const).forEach((k) => {
      if (oldP[k] !== newP[k]) {
        changes[k] = { old: oldP[k as keyof UserProfile], new: newP[k as keyof UserProfile] };
      }
    });
    return changes;
  };

  const safeAudit = async (changes: ChangeMap) => {
    if (!Object.keys(changes).length) return;
    const idToken = localStorage.getItem('idToken');
    if (!idToken) return;
    try {
      await auditProfileChange(idToken, changes);
    } catch (e) {
      console.warn('profile audit failed', e);
    }
  };

  const handleSaveProfile = async () => {
    if (!validateForm()) return;
    const accessToken = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');
    
    if (!accessToken) {
      setError('No access token available');
      return;
    }

    // Build diff BEFORE mutating originalProfile
    const changes = computeChanges(originalProfile, profile);

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Check if user is OAuth user
      const isOAuthUser = idToken && parseJWTToken(idToken)?.identities;

      if (isOAuthUser) {
        // For OAuth users, we can't update attributes via Cognito API
        // Instead, just update the local state and show a message
        
        if (emailChanged) {
          setError('Email changes are not supported for social sign-in accounts. Please contact support if you need to change your email.');
          return;
        }

        // Update local profile state (for display purposes)
        setOriginalProfile(profile);
        setSuccess('Profile updated locally. Note: Some changes may require re-authentication with your social provider.');
        
        // Update user context with combined name
        updateUser({
          name: `${profile.firstName} ${profile.lastName}`.trim(),
          email: profile.email
        });

        // Audit successful profile change
        await safeAudit(changes);

      } else {
        // For regular users, use the existing API approach
        const attributes = [
          { Name: 'given_name', Value: profile.firstName },
          { Name: 'family_name', Value: profile.lastName },
        ];

        if (profile.phoneNumber) {
          attributes.push({ Name: 'phone_number', Value: profile.phoneNumber });
        }

        if (emailChanged) {
          attributes.push({ Name: 'email', Value: profile.email });
          await cognitoAuthService.updateUserAttributes(accessToken, attributes);
          setVerificationDialog(true);
          setSuccess('Profile updated! Please check your new email for verification code.');
          
          // Audit changes including pending email update
          await safeAudit(changes);
        } else {
          await cognitoAuthService.updateUserAttributes(accessToken, attributes);
          setOriginalProfile(profile);
          setSuccess('Profile updated successfully!');
          
          updateUser({
            name: `${profile.firstName} ${profile.lastName}`.trim(),
            email: profile.email
          });

          // Audit successful profile change
          await safeAudit(changes);
        }
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      setSendingVerification(true);
      setError(null);

      await cognitoAuthService.verifyUserAttribute(accessToken, 'email', verificationCode);
      
      // Update profile state
      const updatedProfile = { ...profile, emailVerified: true };
      setProfile(updatedProfile);
      setOriginalProfile(updatedProfile);
      setEmailChanged(false);
      
      // Close dialog and show success
      setVerificationDialog(false);
      setVerificationCode('');
      setSuccess('Email verified successfully!');
      
      // Update user context and refresh profile
      updateUser({
        email: profile.email,
        name: `${profile.firstName} ${profile.lastName}`.trim(),
        emailVerified: true
      });
      
      await refreshUserProfile();
    } catch (err) {
      console.error('Error verifying email:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify email');
    } finally {
      setSendingVerification(false);
    }
  };

  const resendVerificationCode = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      setSendingVerification(true);
      await cognitoAuthService.resendAttributeVerification(accessToken, 'email');
      setSuccess('Verification code sent to your new email address');
    } catch (err) {
      console.error('Error resending verification:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend verification code');
    } finally {
      setSendingVerification(false);
    }
  };

  const hasChanges = () => {
    return (
      profile.firstName !== originalProfile.firstName ||
      profile.lastName !== originalProfile.lastName ||
      profile.email !== originalProfile.email ||
      profile.phoneNumber !== originalProfile.phoneNumber
    );
  };

  const handleCancel = () => {
    if (hasChanges()) {
      setShowUnsavedDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleResetChanges = () => {
    setProfile(originalProfile);
    setEmailChanged(false);
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Profile Settings
        </Typography>
        <Button
          variant="text"
          startIcon={<X size={18} />}
          onClick={handleCancel}
          sx={{ color: 'text.secondary' }}
        >
          Close
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <User size={24} />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Personal Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profile.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profile.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {profile.emailVerified ? (
                            <Chip label="Verified" color="success" size="small" />
                          ) : (
                            <Chip label="Unverified" color="warning" size="small" />
                          )}
                          <Mail size={20} />
                        </Box>
                      )
                    }}
                  />
                  {emailChanged && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      <AlertCircle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      Changing your email will require verification
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={profile.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    InputProps={{
                      endAdornment: <Phone size={20} />
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Security Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Shield size={24} />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Account Security
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Email Verification Status:
                </Typography>
                {profile.emailVerified ? (
                  <Chip label="Verified" color="success" size="small" />
                ) : (
                  <Chip label="Unverified" color="warning" size="small" />
                )}
              </Box>

              {!profile.emailVerified && !emailChanged && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Your email address is not verified. Some features may be limited.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetChanges}
              disabled={!hasChanges() || saving}
              sx={{
                color: hasChanges() ? 'text.primary' : 'text.disabled',
                borderColor: hasChanges() ? 'primary.main' : 'text.disabled',
                '&:hover': hasChanges() ? {
                  backgroundColor: 'primary.50',
                  borderColor: 'primary.main',
                } : {},
              }}
            >
              Reset Changes
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveProfile}
              disabled={!hasChanges() || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <Save size={20} />}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to leave without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedDialog(false)}>Stay</Button>
          <Button onClick={() => navigate(-1)} color="error">
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Verification Dialog */}
      <Dialog open={verificationDialog} onClose={() => setVerificationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify New Email Address</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We've sent a verification code to your new email address: <strong>{profile.email}</strong>
          </Typography>
          
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter 6-digit code"
            inputProps={{ maxLength: 6 }}
          />
          
          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              onClick={resendVerificationCode}
              disabled={sendingVerification}
              size="small"
            >
              {sendingVerification ? 'Sending...' : 'Resend Code'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialog(false)}>Cancel</Button>
          <Button
            onClick={handleVerifyEmail}
            variant="contained"
            disabled={!verificationCode.trim() || sendingVerification}
          >
            {sendingVerification ? 'Verifying...' : 'Verify Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
